import React, { useState, forwardRef, useImperativeHandle } from "react";
import { Snackbar, Alert, Box, Typography, IconButton, keyframes } from "@mui/material";
import PropTypes from "prop-types";
import { Close, SmartToy } from "@mui/icons-material";

// 1. Define a subtle pulse animation for the robot eyes/glow
const pulse = keyframes`
  0% { opacity: 0.7; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.1); }
  100% { opacity: 0.7; transform: scale(1); }
`;

const SnackbarWrapper = forwardRef(
  (
    {
      autoHideDuration = 4000,
      anchorOrigin = { vertical: "bottom", horizontal: "right" }, // 👈 Now configurable
    },
    ref
  ) => {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [severity, setSeverity] = useState("success");

    const handleClose = (event, reason) => {
      if (reason === "clickaway") return;
      setOpen(false);
    };

    useImperativeHandle(ref, () => ({
      showSnackbar(msg, sev = "success") {
        setMessage(msg);
        setSeverity(sev);
        setOpen(true);
      },
    }));

    return (
      <Snackbar
        open={open}
        autoHideDuration={autoHideDuration}
        onClose={handleClose}
        anchorOrigin={anchorOrigin}
      >
        {/* CONDITIONAL RENDERING: Check if it's an AI message */}
        {severity === "ai" ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              minWidth: "300px",
              maxWidth: "450px",
              background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)", // Dark Slate Gradient
              color: "#e2e8f0",
              padding: "12px 20px",
              borderRadius: "12px",
              border: "1px solid #38bdf8", // Electric Blue Border
              boxShadow: "0 8px 32px rgba(56, 189, 248, 0.25)", // Blue Neon Glow
              fontFamily: "'Roboto Mono', monospace", // Techy Font
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Decorative background glow element */}
            <Box
              sx={{
                position: "absolute",
                top: "-50%",
                left: "-20%",
                width: "100px",
                height: "100px",
                background: "radial-gradient(circle, rgba(56,189,248,0.4) 0%, rgba(0,0,0,0) 70%)",
                zIndex: 0,
              }}
            />

            {/* AI Robot Icon */}
            <Box
              sx={{
                zIndex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(56, 189, 248, 0.1)",
                borderRadius: "50%",
                padding: "8px",
              }}
            >
              <SmartToy
                sx={{
                  color: "#38bdf8", // Electric Blue
                  fontSize: 28,
                  animation: `${pulse} 2s infinite ease-in-out`,
                }}
              />
            </Box>

            {/* Message Content */}
            <Box sx={{ zIndex: 1, flex: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: "#94a3b8",
                  textTransform: "uppercase",
                  fontSize: "0.7rem",
                  letterSpacing: "1px",
                  fontWeight: "bold",
                  display: "block",
                  mb: 0.5,
                }}
              >
                AI Assistant
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: "0.9rem",
                  lineHeight: 1.4,
                  fontWeight: 500,
                }}
              >
                {message}
              </Typography>
            </Box>

            {/* Close Button (Custom styled) */}
            <IconButton
              size="small"
              onClick={handleClose}
              sx={{ color: "#64748b", zIndex: 1, "&:hover": { color: "#fff" } }}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        ) : (
          /* STANDARD ALERT for success/error/warning */
          <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
            {message}
          </Alert>
        )}
      </Snackbar>
    );
  }
);

SnackbarWrapper.propTypes = {
  autoHideDuration: PropTypes.number,
  anchorOrigin: PropTypes.shape({
    vertical: PropTypes.oneOf(["top", "bottom"]).isRequired,
    horizontal: PropTypes.oneOf(["left", "center", "right"]).isRequired,
  }),
};

export default SnackbarWrapper;
